package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.StatementRowDisplay
import com.example.mystybag.appCore.model.pages.StatementsUIState
import com.example.mystybag.appCore.expected.StatementsPdfExporter
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.format
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantDashboardRequestModel
import com.example.mystybag.sharedmodels.project.logic.enums.ReservationStatus
import com.example.mystybag.sharedmodels.project.responseModels.merchant.ReservationItem
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime

class StatementsViewModel(context: AppContext) : ChildViewModel<StatementsUIState>(context, StatementsUIState()) {

    companion object {
        /** Inclusive range end; start is [defaultPeriodDays] calendar days before that (same idea as dashboard „last week“). */
        private const val defaultPeriodDays = 7
    }
    private data class StatementRecord(
        val id: Long,
        val bagName: String,
        val saleDate: LocalDate?,
        val saleDateDisplay: String,
        val amount: Double
    )

    private var allRecords: List<StatementRecord> = emptyList()

    override fun onCreate() {
        navControls.title = sharedString(SR.strings.statements)
        navControls.backButtonVisible = true
        navControls.additionalIcons = emptyList()
        val today = todayInDeviceTimeZone()
        val fromDefault = today.minus(DatePeriod(days = defaultPeriodDays))
        updateState { it.copy(filterDateFrom = fromDefault, filterDateTo = today) }
        loadStatements()
    }

    private fun todayInDeviceTimeZone(): LocalDate {
        val ldt = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        return LocalDate(ldt.year, ldt.monthNumber, ldt.dayOfMonth)
    }

    fun onBackClick() {
        goBack()
    }

    fun onShareStatements() {
        val state = uiState.value
        if (state.rows.isEmpty()) {
            showSnackbar(SR.strings.statements_no_rows)
            return
        }
        val isSuccess = StatementsPdfExporter.exportAndShareStatementsPdf(
            documentTitle = sharedString(SR.strings.statements),
            totalAmount = state.totalAmountFormatted,
            reportPeriod = state.reportPeriodDisplay,
            currency = sharedString(SR.strings.currency_eur),
            rows = state.rows
        )
        if (!isSuccess) {
            showSnackbar(SR.strings.oops_something_went_wrong)
        }
    }

    fun onFilterDateFromChanged(date: LocalDate?) {
        val currentTo = uiState.value.filterDateTo
        val adjustedTo = if (currentTo != null && date != null && currentTo < date) date else currentTo
        updateState { it.copy(filterDateFrom = date, filterDateTo = adjustedTo) }
        applyCurrentFilters()
    }

    fun onFilterDateToChanged(date: LocalDate?) {
        val currentFrom = uiState.value.filterDateFrom
        val adjustedFrom = if (currentFrom != null && date != null && currentFrom > date) date else currentFrom
        updateState { it.copy(filterDateFrom = adjustedFrom, filterDateTo = date) }
        applyCurrentFilters()
    }

    fun loadStatements() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) {
            updateState { it.copy(isLoading = false) }
            return
        }

        scope.launch {
            updateState { it.copy(isLoading = true) }
            try {
                commandBlock(showProgress = false, hideProgress = false) {
                    CommonCommands.getMerchantDashboard(GetMerchantDashboardRequestModel(merchantId = merchantId))
                        .onResponse(
                            success = { response ->
                                launchOnMain {
                                    val model = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantDashboardResponseModel
                                    var reservations = model?.reservations ?: emptyList()
                                    val isStaff = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
                                    val staffLocationIds = if (isStaff) {
                                        Preferences.getString(key = PREFERENCES_STAFF_LOCATION_IDS, "")
                                            .split(",").mapNotNull { it.trim().toLongOrNull() }.filter { it > 0L }
                                    } else emptyList()
                                    if (staffLocationIds.isNotEmpty()) {
                                        reservations = reservations.filter { (it.locationId ?: 0L) in staffLocationIds }
                                    }

                                    val paidToMerchant = reservations.filter { isPaidToMerchant(it) }
                                        .sortedByDescending { it.id ?: 0L }

                                    val bagDefault = sharedString(SR.strings.surprise_bag)

                                    allRecords = paidToMerchant.map { r ->
                                        val saleDate = parseReservationDate(r.dateTimeReserved)
                                        StatementRecord(
                                            id = r.id ?: 0L,
                                            bagName = r.bagName?.takeIf { it.isNotBlank() } ?: bagDefault,
                                            saleDate = saleDate,
                                            saleDateDisplay = saleDate?.let { formatDate(it) } ?: "—",
                                            amount = r.amount ?: 0.0
                                        )
                                    }

                                    applyCurrentFilters(isLoading = false)
                                }
                            },
                            error = {
                                launchOnMain {
                                    updateState { s -> s.copy(isLoading = false) }
                                }
                            },
                            baseError = {
                                launchOnMain {
                                    updateState { s -> s.copy(isLoading = false) }
                                }
                            }
                        )
                }
            } catch (_: Exception) {
                launchOnMain {
                    updateState { s -> s.copy(isLoading = false) }
                }
            }
        }
    }

    private fun isPaidToMerchant(reservation: ReservationItem): Boolean {
        val code = reservation.status ?: return false
        return ReservationStatus.fromInt(code) == ReservationStatus.PAID_TO_MERCHANT
    }

    private fun applyCurrentFilters(isLoading: Boolean = uiState.value.isLoading) {
        val from = uiState.value.filterDateFrom
        val to = uiState.value.filterDateTo
        val filtered = allRecords.filter { record ->
            val d = record.saleDate
            val fromOk = from == null || (d != null && d >= from)
            val toOk = to == null || (d != null && d <= to)
            fromOk && toOk
        }

        val total = filtered.sumOf { it.amount }
        val totalFormatted = format("%.2f", total)
        val rows = filtered.map { r ->
            StatementRowDisplay(
                id = r.id,
                bagName = r.bagName,
                saleDateDisplay = r.saleDateDisplay,
                amountFormatted = format("%.2f", r.amount),
            )
        }
        val reportPeriod = buildReportPeriodDisplay(filtered.mapNotNull { it.saleDate }, from, to)

        updateState {
            it.copy(
                isLoading = isLoading,
                rows = rows,
                totalAmountFormatted = totalFormatted,
                reportPeriodDisplay = reportPeriod
            )
        }
    }

    private fun formatDate(date: LocalDate): String {
        val d = date.dayOfMonth
        val m = date.monthNumber
        val y = date.year
        return "${d.toString().padStart(2, '0')}.${m.toString().padStart(2, '0')}.$y"
    }

    private fun buildReportPeriodDisplay(
        dates: List<LocalDate>,
        from: LocalDate?,
        to: LocalDate?
    ): String {
        if (from != null || to != null) {
            val startText = from?.let { formatDate(it) } ?: "…"
            val endText = to?.let { formatDate(it) } ?: "…"
            return if (from != null && to != null && from == to) startText else "$startText - $endText"
        }
        if (dates.isEmpty()) return "—"
        val min = dates.minOrNull() ?: return "—"
        val max = dates.maxOrNull() ?: return "—"
        if (min == max) return formatDate(min)
        return "${formatDate(min)} - ${formatDate(max)}"
    }

    private fun parseReservationDate(dateTimeReserved: String?): LocalDate? {
        if (dateTimeReserved.isNullOrBlank()) return null
        return try {
            val ms = dateTimeReserved.trim().toLongOrNull()
            if (ms != null) {
                val instant = Instant.fromEpochMilliseconds(
                    if (ms < 1_000_000_000_000L) ms * 1000L else ms
                )
                val ldt = instant.toLocalDateTime(TimeZone.currentSystemDefault())
                LocalDate(ldt.year, ldt.monthNumber, ldt.dayOfMonth)
            } else {
                LocalDate.parse(dateTimeReserved.take(10))
            }
        } catch (_: Exception) {
            null
        }
    }
}
